import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Button, ButtonGroup, Spinner } from 'react-bootstrap';
import { FaTimes, FaCheck, FaTimes as FaTimesIcon } from 'react-icons/fa';
import { toast } from 'react-toastify';
import PedidosYa from './components/PedidosYa/PedidosYa';
import Rappi from './components/Rappi/Rappi';
import Uber from './components/Uber/Uber';
import { toggleAutoAcceptOrdersByLocalId, getLocalConfigurationByLocalId } from '../../../../../../crud/management_api/local_configurations';
import './DeliveryConfig.scss';

interface DeliveryConfigProps {
  onClose: () => void;
}

const DeliveryConfig: React.FC<DeliveryConfigProps> = ({ onClose }) => {
  const [autoAcceptOrders, setAutoAcceptOrders] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [configLoading, setConfigLoading] = useState(true);

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const localId = user.local_id;

  const loadLocalConfiguration = useCallback(async () => {
    try {
      setConfigLoading(true);
      const response = await getLocalConfigurationByLocalId(localId);
      setAutoAcceptOrders(response.data.auto_accept_orders || false);
    } catch (error: any) {
      console.error('Error loading local configuration:', error);
      toast.error('Error cargando configuración local');
    } finally {
      setConfigLoading(false);
    }
  }, [localId]);

  useEffect(() => {
    if (localId) {
      loadLocalConfiguration();
    }
  }, [localId, loadLocalConfiguration]);

  const handleAutoAcceptToggle = async () => {
    if (loading) return;
    
    setLoading(true);
    try {
      await toggleAutoAcceptOrdersByLocalId(localId);
      setAutoAcceptOrders(!autoAcceptOrders);
      toast.success(
        `Aceptación automática de pedidos ${!autoAcceptOrders ? 'activada' : 'desactivada'}`
      );
    } catch (error: any) {
      console.error('Error toggling auto accept orders:', error);
      toast.error(`Error ${!autoAcceptOrders ? 'activando' : 'desactivando'} aceptación automática`);
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="delivery-config">
      <Container fluid>
        <div className="delivery-config-header">
          <Row className="align-items-center">
            <Col>
              <h2 className="delivery-config-title">Configuración de Delivery</h2>
            </Col>
            <Col xs="auto">
              <Button 
                variant="outline-secondary" 
                size="sm"
                onClick={onClose}
                className="close-config-btn"
              >
                <FaTimes /> Cerrar
              </Button>
            </Col>
          </Row>
        </div>

        <div className="auto-accept-section">
          <div className="auto-accept-card">
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center">
                <div>
                  <h6 className="mb-1 fw-medium">Aceptación Automática de Pedidos</h6>
                  <small className="text-muted">
                    Acepta automáticamente todos los pedidos de delivery sin intervención manual
                  </small>
                </div>
              </div>
              <div className="d-flex align-items-center">
                {configLoading ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  <div className="auto-accept-toggle">
                    <ButtonGroup className="toggle-group">
                      <Button
                        variant={autoAcceptOrders ? "success" : "outline-secondary"}
                        size="sm"
                        onClick={() => !autoAcceptOrders && handleAutoAcceptToggle()}
                        disabled={loading || autoAcceptOrders}
                        className={`toggle-btn ${autoAcceptOrders ? 'active' : ''}`}
                      >
                        {loading && !autoAcceptOrders ? (
                          <Spinner animation="border" size="sm" />
                        ) : (
                          <>
                            <FaCheck className="me-1" />
                            Activo
                          </>
                        )}
                      </Button>
                      <Button
                        variant={!autoAcceptOrders ? "danger" : "outline-secondary"}
                        size="sm"
                        onClick={() => autoAcceptOrders && handleAutoAcceptToggle()}
                        disabled={loading || !autoAcceptOrders}
                        className={`toggle-btn ${!autoAcceptOrders ? 'active' : ''}`}
                      >
                        {loading && autoAcceptOrders ? (
                          <Spinner animation="border" size="sm" />
                        ) : (
                          <>
                            <FaTimesIcon className="me-1" />
                            Inactivo
                          </>
                        )}
                      </Button>
                    </ButtonGroup>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        
        <div className="delivery-config-content">
          <PedidosYa />
          <Rappi />
          <Uber />
        </div>
      </Container>
    </div>
  );
};

export default DeliveryConfig;